import React, { useState, useEffect } from "react";
import DynamicBackground from "../../components/DynamicBackground";
import "../../styles/darkMode.css";
import "./Dictionary.css";
import { fetchTerms } from "../../services/dictionary.service";

export interface Term {
  word: string;
  definition: string;
  category: string;
  example?: string;
}

const Dictionary: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [terms, setTerms] = useState<Term[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTerms = async () => {
      try {
        const fetchedTerms = await fetchTerms();
        setTerms(fetchedTerms);
      } catch (err) {
        setError('Failed to load terms. Please try again later.');
        console.error('Error loading terms:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTerms();
  }, []);

  const categories = ['all', ...new Set(terms.map(term => term.category))];

  const filteredTerms = terms.filter(term => {
    const matchesSearch = term.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (isLoading) {
    return (
      <DynamicBackground>
        <div className="dark-container">
          <h1 className="dark-title">Music Law Dictionary</h1>
          <div className="dark-text">Loading terms...</div>
        </div>
      </DynamicBackground>
    );
  }

  if (error) {
    return (
      <DynamicBackground>
        <div className="dark-container">
          <h1 className="dark-title">Music Law Dictionary</h1>
          <div className="dark-text-muted">{error}</div>
        </div>
      </DynamicBackground>
    );
  }

  return (
    <DynamicBackground>
      <div className="dark-container">
        <h1 className="dark-title">Music Law Dictionary</h1>
        <div className="searchSection">
          <input
            type="text"
            placeholder="Search terms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dark-input searchInput"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="dark-select categorySelect"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="termsGrid">
          {filteredTerms.map((term, index) => (
            <div key={index} className="dark-card">
              <h3 className="dark-text">{term.word}</h3>
              <p className="dark-text-muted category">{term.category}</p>
              <p className="dark-text definition">{term.definition}</p>
              {term.example && <p className="dark-text-muted example">Example: {term.example}</p>}
            </div>
          ))}
        </div>
      </div>
    </DynamicBackground>
  );
};

export default Dictionary; 